import { useEffect, useRef, useState } from 'react';
import { IAnyType } from 'common/types/common';
import { normalizeLocalizedHtml } from 'common/utils/url';
import gsap from 'gsap/dist/gsap';
import ScrollTrigger from 'gsap/dist/ScrollTrigger';
import { useTranslation } from 'next-i18next';

gsap.registerPlugin(ScrollTrigger);

export const ServiceAbout: React.FC<{ translationKey: string }> = ({ translationKey }) => {
  const { t, i18n } = useTranslation('pageServices');
  const sectionRef = useRef<HTMLElement | null>(null);
  const [isLargeScreen, setIsLargeScreen] = useState(false);
  const normalizeHtml = (value: string) => normalizeLocalizedHtml(value, i18n.language);

  const sections = t(`serviceSingle.${translationKey}.about.sections`, {
    returnObjects: true,
  }) as IAnyType[];
  const introTitle = t(`serviceSingle.${translationKey}.about.introTitle`);
  const introText = t(`serviceSingle.${translationKey}.about.introText`);
  const outroTitle = t(`serviceSingle.${translationKey}.about.outroTitle`);
  const outroText = t(`serviceSingle.${translationKey}.about.outroText`);

  useEffect(() => {
    if (typeof window === 'undefined') return;

    const mediaQuery = window.matchMedia('(min-width: 1024px)');
    const syncBreakpoint = () => {
      setIsLargeScreen(mediaQuery.matches);
    };

    syncBreakpoint();
    mediaQuery.addEventListener('change', syncBreakpoint);

    return () => {
      mediaQuery.removeEventListener('change', syncBreakpoint);
    };
  }, []);

  useEffect(() => {
    if (!sectionRef.current || !sections.length || !isLargeScreen) return;

    const ctx = gsap.context(() => {
      const cards = gsap.utils.toArray<HTMLElement>('[data-service-about-card]');

      cards.forEach((card, index) => {
        gsap.set(card, {
          transformOrigin: 'center top',
          zIndex: index + 1,
        });
      });

      cards.forEach((card, index) => {
        const currentPanel = document.querySelector(`#about-panel-${index + 1}`);
        if (!currentPanel) return;

        gsap.fromTo(
          card,
          { scale: 1 },
          {
            scale: 0.96,
            ease: 'none',
            scrollTrigger: {
              trigger: currentPanel,
              start: 'top 10%',
              end: 'top 0%',
              scrub: true,
            },
          },
        );
      });
    }, sectionRef);

    return () => ctx.revert();
  }, [isLargeScreen, sections]);

  return (
    <>
      <section className="bg-gradient relative py-20 lg:py-32 pt-12 lg:pt-20">
        <div className="container grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <h2
            className="text-heading-about font-black text-white"
            dangerouslySetInnerHTML={{ __html: normalizeHtml(introTitle) }}
          />
          <div />
          <div />
          <p
            className="max-w-[80ch] text-lg"
            dangerouslySetInnerHTML={{ __html: normalizeHtml(introText) }}
          />
        </div>
      </section>
      <section ref={sectionRef} className="bg-gradient relative">
        <div className="container grid gap-6">
          {sections.map((item: IAnyType, index: number) => (
            <article
              key={item.title}
              id={`about-panel-${index + 1}`}
              data-service-about-card
              className="rounded-[30px] border border-white/10 bg-[#21172d] will-change-transform lg:sticky"
              style={{ zIndex: index + 1, top: isLargeScreen ? 85 : undefined }}>
              <div className="flex items-center justify-between gap-4 p-6 lg:px-10">
                <div className="flex items-center gap-3">
                  <span className="h-px w-10 bg-primary" />
                  <h3
                    className="font-semibold uppercase text-xs"
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(item.title) }}
                  />
                </div>
              </div>

              <div className="grid lg:grid-cols-[minmax(0,0.84fr)_minmax(0,1.16fr)]">
                <div className="flex h-full flex-col justify-between p-6 pt-0 pb-6 lg:px-10 lg:pb-10">
                  <h4
                    className="max-w-[24ch] font-black text-primary text-xl font-black leading-[2rem] lg:text-2xl lg:leading-[2.2rem]"
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(item.quote) }}
                  />
                  <div className="mt-10 space-y-4 text-xs uppercase">
                    {`${index + 1}/${sections.length}`}
                  </div>
                </div>

                <div className="flex h-full flex-col px-6 py-6 lg:px-10 lg:py-10">
                  <div
                    className="mt-3 max-w-[95%] text-white/84 [&_b]:font-semibold [&_ul]:mt-5 [&_ul]:space-y-2 [&_ul]:pl-5 [&_ul]:text-white [&_li]:list-disc [&_li]:marker:text-primary"
                    dangerouslySetInnerHTML={{ __html: normalizeHtml(item.text) }}
                  />
                </div>
              </div>
            </article>
          ))}
        </div>
      </section>

      <section className="bg-gradient relative py-20 lg:py-48">
        <div className="container grid lg:grid-cols-[minmax(0,0.9fr)_minmax(0,1.1fr)]">
          <h2
            className="text-heading-about font-black text-white"
            dangerouslySetInnerHTML={{ __html: normalizeHtml(outroTitle) }}
          />
          <div />
          <div />
          <p
            className="max-w-[80ch] text-lg"
            dangerouslySetInnerHTML={{ __html: normalizeHtml(outroText) }}
          />
        </div>
      </section>
    </>
  );
};
